import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Container,
  Box,
  useTheme,
  useMediaQuery,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import MapIcon from '@mui/icons-material/Map';
import AddIcon from '@mui/icons-material/Add';
import EmailIcon from '@mui/icons-material/Email';
import CloseIcon from '@mui/icons-material/Close';
import LocalBarIcon from '@mui/icons-material/LocalBar';
import SuggestDealDialog from './SuggestDealDialog';
import { useCityContext } from '../contexts/CityContext';

const Header = ({ onToggleMap }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { city } = useCityContext();
  const [suggestDealOpen, setSuggestDealOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Menu items configuration - dynamic based on city
  const menuItems = [
    { text: 'Map', onClick: onToggleMap, icon: <MapIcon /> },
    { text: 'Suggest Deal', onClick: () => setSuggestDealOpen(true), icon: <AddIcon /> },
    { text: 'Contact', href: `mailto:${city.contactEmail}`, icon: <EmailIcon /> }
  ];

  const renderMobileMenu = () => (
    <Drawer
      anchor="right"
      open={mobileMenuOpen}
      onClose={() => setMobileMenuOpen(false)}
      PaperProps={{
        sx: {
          width: 280,
          backgroundColor: 'background.warm',
          pt: 2
        }
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2, mb: 2 }}>
        <IconButton onClick={() => setMobileMenuOpen(false)}>
          <CloseIcon />
        </IconButton>
      </Box>
      <List>
        {menuItems.map((item) => (
          <ListItem
            button
            key={item.text}
            onClick={() => {
              if (item.onClick) item.onClick();
              else if (item.href) window.open(item.href, item.target || '_self');
              setMobileMenuOpen(false);
            }}
            sx={{
              py: 2,
              px: 3,
              '&:hover': {
                backgroundColor: 'rgba(217, 119, 6, 0.08)'
              }
            }}
          >
            <ListItemIcon sx={{ color: 'primary.main', minWidth: 40 }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText
              primary={item.text}
              primaryTypographyProps={{
                fontWeight: 500,
                color: 'text.primary'
              }}
            />
          </ListItem>
        ))}
      </List>
    </Drawer>
  );

  return (
    <>
      {/* Sticky Navigation Bar */}
      <AppBar
        position="sticky"
        elevation={2}
        sx={{
          backgroundColor: 'background.warm',
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }}>
          <Box
            component={Link}
            to="/"
            sx={{
              display: 'flex',
              alignItems: 'center',
              textDecoration: 'none',
              gap: 1
            }}
          >
            <LocalBarIcon sx={{ color: 'primary.main', fontSize: 28 }} />
            <Typography
              variant="h6"
              sx={{
                color: 'primary.main',
                fontWeight: 700,
                letterSpacing: '-0.5px'
              }}
            >
              {city.displayName}
            </Typography>
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          {isMobile ? (
            <IconButton
              edge="end"
              aria-label="menu"
              onClick={() => setMobileMenuOpen(true)}
              sx={{ color: 'text.primary' }}
            >
              <MenuIcon />
            </IconButton>
          ) : (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {menuItems.map((item) => (
                <Button
                  key={item.text}
                  onClick={item.onClick}
                  href={item.href}
                  target={item.target}
                  startIcon={item.icon}
                  sx={{
                    color: 'text.primary',
                    fontWeight: 500,
                    px: 2,
                    '&:hover': {
                      backgroundColor: 'rgba(217, 119, 6, 0.08)',
                      color: 'primary.main'
                    }
                  }}
                >
                  {item.text}
                </Button>
              ))}
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {/* Mobile drawer menu */}
      {isMobile && renderMobileMenu()}

      <SuggestDealDialog
        open={suggestDealOpen}
        onClose={() => setSuggestDealOpen(false)}
      />

      {/* Compact Hero section with background image */}
      <Box
        sx={{
          height: isMobile ? '150px' : '200px',
          backgroundImage: 'url(/images/cover_long.jpg)',
          backgroundPosition: 'center',
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
        }}
      >
        {/* Warm gradient overlay */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'linear-gradient(to bottom, rgba(45, 42, 38, 0.4), rgba(217, 119, 6, 0.3))',
            zIndex: 1,
          }}
        />

        {/* Title content centered in hero */}
        <Container maxWidth="lg" sx={{ display: 'flex', flexGrow: 1, position: 'relative', zIndex: 2 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              margin: 'auto',
              textShadow: '2px 2px 8px rgba(0,0,0,0.6)'
            }}
          >
            <Typography
              variant="h1"
              component="h1"
              align="center"
              sx={{
                fontSize: isMobile ? '28px' : '38px',
                color: 'white',
                fontWeight: 700,
                letterSpacing: '-0.5px'
              }}
            >
              {city.displayName}
            </Typography>
            <Typography
              variant="h2"
              component="h2"
              align="center"
              sx={{
                fontSize: isMobile ? '14px' : '18px',
                color: 'rgba(255, 255, 255, 0.9)',
                mt: 1,
                fontWeight: 400
              }}
            >
              {city.tagline}
            </Typography>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default Header;
